import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useRef } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Tooltip from "@mui/material/Tooltip";
import Navbar from "../globals/components/Navbar";
import { apiClient } from "../globals/utils/api";
import { buildBondStatusPatchPayload } from "../globals/utils/bondStatusPatchUtils";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import NumberFormatCustom from "../globals/utils/Bond Creation Utils/numberFormatCustom";
import BarChartIcon from '@mui/icons-material/BarChart';
import TablePaginationActions from "../globals/components/TablePaginationActions";

const DOC_GEN_STEPS = [
  { key: "queued", label: "Queued" },
  { key: "template_download", label: "Downloading Templates..." },
  { key: "doc_fill", label: "Filling Documents..." },
  { key: "pdf_conversion", label: "Converting to PDF..." },
  { key: "merge", label: "Merging Documents..." },
  { key: "ams", label: "Creating Policy..." },
  { key: "upload_and_notify", label: "Uploading & Notifying..." },
];

function getStepIndex(step) {
  const idx = DOC_GEN_STEPS.findIndex((s) => s.key === step);
  return idx === -1 ? 0 : idx;
}

function DocGenProgress({ step }) {
  const activeIdx = getStepIndex(step);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {DOC_GEN_STEPS.map((s, i) => (
        <Box
          key={s.key}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor:
              i < activeIdx
                ? "success.main"
                : i === activeIdx
                ? "info.main"
                : "grey.300",
            animation: i === activeIdx ? "pulse 1.4s infinite" : "none",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.3 },
            },
          }}
        />
      ))}
    </Stack>
  );
}

function formatDuration(minutes) {
  if (minutes == null) return "-";
  if (minutes < 1) return "<1m";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatApprovedAt(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
    hour12: true,
  });
}

const bondTypes = ["Probate", "Guardianship", "Blanket", "VA", "Trustee"];

const bondTabs = [
  "Active",
  "Approved",
  "Rejected",
  "To Be Cancelled",
  "Data Corrections",
];

const getUnderwritingChip = (statusRaw) => {
  const status = (statusRaw || "").toString().trim().toLowerCase();
  if (status === "pending") return { label: "Pending", color: "warning" };
  if (status === "approved") return { label: "Approved", color: "success" };
  if (status === "not required") return { label: "Not Required", color: "default" };
  if (status === "overrided") return { label: "Not Required", color: "default" };
  if (status) return { label: status.charAt(0).toUpperCase() + status.slice(1), color: "default" };
  return { label: "-", color: "default" };
};

const getBondRetrieveUrl = (bondType, bondId) => {
  if (!bondId) return null;
  const type = (bondType || "").toString().trim();
  if (type === "VA") return `/vermost-va-bond-update/${bondId}`;
  if (type === "Trustee") return `/vermost-trustee-bond-update/${bondId}`;
  if (type === "Probate") return `/vermost-probate-bond-update/${bondId}`;
  if (type === "Blanket") return `/vermost-blanket-bond-update/${bondId}`;
  if (type === "Guardianship") return `/vermost-guardianship-bond-update/${bondId}`;
  return `/vermost-guardianship-bond-update/${bondId}`;
};

/** Human-readable labels for correction field names (snake_case -> Title Case). */
const CORRECTION_FIELD_LABELS = {
  bond_amount: "Bond Amount",
  bond_number: "Bond Number",
  bond_sub_category: "Bond Sub Category",
  premium: "Premium",
  court_case_number: "Court Case Number",
  state: "State",
  county: "County",
  name: "Name",
  first_document_name: "First Document Name",
  second_document_name: "Second Document Name",
  third_document_name: "Third Document Name",
  requester_name: "Requester Name",
  requester_user_email: "Requester Email",
  requester_phone_number: "Requester Phone",
  is_underwriting: "Is Underwriting",
  managing_lawfirm: "Managing Law Firm",
  law_firm: "Law Firm",
  law_firm_location: "Law Firm Location",
  obligee: "Obligee",
  bond_carrier: "Bond Carrier",
  bond_type: "Bond Type",
  status: "Status",
  billing_method: "Billing Method",
};
/** Format correction field key for display: use label map or convert snake_case to Title Case. */
const formatCorrectionFieldLabel = (field) => {
  const key = (field ?? "").toString().trim().toLowerCase().replace(/\s+/g, "_");
  if (!key) return "—";
  if (CORRECTION_FIELD_LABELS[key]) return CORRECTION_FIELD_LABELS[key];
  return key.split("_").map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : "")).join(" ");
};

/** Format correction From/To for display: bond_amount uses US style with commas, no decimals. */
const formatCorrectionFromTo = (field, value) => {
  const f = (field ?? "").toString().toLowerCase().replace(/\s+/g, "_");
  if (f === "bond_amount") {
    const n = Number(value);
    return Number.isNaN(n) ? (value ?? "").toString() : n.toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }
  return (value ?? "").toString();
};

const ENDORSEMENT_NAME_CHANGE_TYPES = ["Name_Change_Principal", "Name_Change_County", "Name_Change_Deceased_Ward_Beneficiary"];
const isNameChangeEndorsement = (t) => ENDORSEMENT_NAME_CHANGE_TYPES.includes(t);

const DWB_LABEL_MAP = {
  Probate: { prefix: "Deceased Legal", alias: "Alias (Deceased)" },
  Guardianship: { prefix: "Ward", alias: "Ward Alias" },
  VA: { prefix: "Beneficiary", alias: "Beneficiary Alias" },
  Trustee: { prefix: "Full Legal Name", single: true },
};

function ApproveBondsPage() {
  const navigate = useNavigate();
  const [selectedType, setselectedType] = useState("Probate"); // Default to Probate
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [bonds, setBonds] = useState([]);
  const [toBeCancelledBonds, setToBeCancelledBonds] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [activeCounts, setActiveCounts] = useState({});
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(50);
  const [hasMore, setHasMore] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [endorsementDialogOpen, setEndorsementDialogOpen] = useState(false);
  const [selectedBondToEndorse, setSelectedBondToEndorse] = useState(null);
  const [endorsementType, setEndorsementType] = useState("Increase");
  const [newBondAmount, setNewBondAmount] = useState("");
  const [endorsementFile, setEndorsementFile] = useState(null);
  const [isSubmittingEndorsement, setIsSubmittingEndorsement] = useState(false);
  const userdetails = useSelector((state) => state.auth.user);
  const role = useSelector((state) => state.auth.role);
  const [contactName, setContactName] = useState(userdetails.first_name + " " + userdetails.last_name);
  const [contactEmail, setContactEmail] = useState(userdetails.email);
  const [updatedName, setUpdatedName] = useState("");
  const [counties, setCounties] = useState([]);
  const [endorsementToCounty, setEndorsementToCounty] = useState("");
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [estimatedAssets, setEstimatedAssets] = useState("");
  const [dwbFirstName, setDwbFirstName] = useState("");
  const [dwbMiddleName, setDwbMiddleName] = useState("");
  const [dwbLastName, setDwbLastName] = useState("");
  const [dwbSuffix, setDwbSuffix] = useState("");
  const [dwbAlias, setDwbAlias] = useState("");

  const [selectedBondToCancel, setSelectedBondToCancel] = useState(null); // State for selected bond to cancel
  const [cancellationFile, setCancellationFile] = useState(null); // State for cancellation file
  const [isMarkingCancellationComplete, setIsMarkingCancellationComplete] = useState(false);
  const [dataCorrectionsList, setDataCorrectionsList] = useState([]);
  const [dataCorrectionsLoading, setDataCorrectionsLoading] = useState(false);
  const dataCorrectionsFetched = useRef(false);
  const [selectedDataCorrection, setSelectedDataCorrection] = useState(null);
  const [dataCorrectionDetailOpen, setDataCorrectionDetailOpen] = useState(false);
  const [completingCorrectionId, setCompletingCorrectionId] = useState(null);
  const isFirstRender = useRef(true);
  const [docGenGroups, setDocGenGroups] = useState([]);
  const [statsOpen, setStatsOpen] = useState(false);

  const docGenFlat = useMemo(
    () => docGenGroups.flatMap((g) => (Array.isArray(g.items) ? g.items : [])),
    [docGenGroups]
  );

  const fetchDocGenStatus = useCallback(async () => {
    try {
      const res = await apiClient("/doc-generation-status/", "GET");
      if (res?.groups && Array.isArray(res.groups)) {
        setDocGenGroups(res.groups);
      } else if (Array.isArray(res)) {
        setDocGenGroups([{ date: "", display_date: "Recent", items: res }]);
      } else {
        setDocGenGroups([]);
      }
    } catch (e) {
      console.error("Failed to fetch doc generation status:", e);
    }
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchBonds(0, 50);
      fetchToBeCancelledBonds();
    }
  }, []);

  useEffect(() => {
    fetchDocGenStatus();
    const intervalMs = statsOpen ? 4000 : 10000;
    const interval = setInterval(fetchDocGenStatus, intervalMs);
    return () => clearInterval(interval);
  }, [fetchDocGenStatus, statsOpen]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchDocGenStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchDocGenStatus]);

  // Fetch all bond data corrections once when page mounts
  useEffect(() => {
    if (dataCorrectionsFetched.current) return;
    dataCorrectionsFetched.current = true;
    setDataCorrectionsLoading(true);
    apiClient("/bond-data-corrections/", "GET")
      .then((res) => setDataCorrectionsList(Array.isArray(res) ? res : []))
      .catch((e) => {
        console.error("Failed to fetch bond data corrections:", e);
        setDataCorrectionsList([]);
      })
      .finally(() => setDataCorrectionsLoading(false));
  }, []);

  const getCorrectionId = (row) => row?.correction_id ?? row?.id;
  const updateCorrectionStatusToComplete = async (row) => {
    const correctionId = getCorrectionId(row);
    if (correctionId == null) {
      toast.error("Cannot update: correction ID missing");
      return;
    }
    setCompletingCorrectionId(correctionId);
    try {
      await apiClient(`/bond-data-corrections/${correctionId}/status/`, "PATCH", { status: "Complete" });
      toast.success("Status updated to Complete");
      setDataCorrectionsList((prev) =>
        prev.map((r) => (getCorrectionId(r) === correctionId ? { ...r, status: "Complete" } : r))
      );
      if (selectedDataCorrection && getCorrectionId(selectedDataCorrection) === correctionId) {
        setSelectedDataCorrection((prev) => (prev ? { ...prev, status: "Complete" } : null));
      }
    } catch (e) {
      toast.error(e?.message || "Failed to update status");
    } finally {
      setCompletingCorrectionId(null);
    }
  };

  const bondStateId = selectedBondToEndorse?.state ?? 6;
  useEffect(() => {
    if (!endorsementDialogOpen || !bondStateId) return;
    const fetchCounties = async () => {
      setLoadingCounties(true);
      try {
        const countyResponse = await apiClient(
          `/counties-by-state/${bondStateId}/`,
          "GET"
        );
        const formatted = (countyResponse || []).map((c) => ({
          label: c.bond_county,
          value: c.bond_county,
        }));
        setCounties(formatted);
      } catch {
        setCounties([]);
      } finally {
        setLoadingCounties(false);
      }
    };
    fetchCounties();
  }, [endorsementDialogOpen, bondStateId]);

  const refreshData = () => {
    setBonds([]);
    setToBeCancelledBonds([]);
    fetchBonds(0, 50);
    fetchToBeCancelledBonds();
    fetchDocGenStatus();
    setStart(0);
    setEnd(50);
    setPage(0);
  };

  useEffect(() => {
    // Reset tabIndex if it's out of bounds
    if (tabIndex >= bondTabs.length) {
      setTabIndex(0);
    }
  }, [tabIndex]);

  const calculateStatusCounts = useCallback((bondsData, bondType) => {
    if (!bondType) return;

    const counts = bondTabs.reduce((acc, status) => {
      if (status.toLowerCase() === "to be cancelled") {
        // Count "To Be Cancelled" bonds by selected type
        acc[status.toLowerCase()] = toBeCancelledBonds.filter(
          (bond) => !bondType || bond.bond_type === bondType
        ).length;
      } else {
        // Count from regular bonds data
        acc[status.toLowerCase()] = bondsData.filter(
          (bond) =>
            bond.bond_type === bondType && bond.status.toLowerCase() === status.toLowerCase()
        ).length;
      }
      return acc;
    }, {});
    setStatusCounts(counts);
  }, [toBeCancelledBonds]);

  const calculateActiveCounts = useCallback((bondsData) => {
    const counts = bondTypes.reduce((acc, type) => {
      const activeCount = bondsData.filter(
        (bond) => bond.bond_type === type && bond.status.toLowerCase() === "active"
      ).length;
      const toBeCancelledCount = toBeCancelledBonds.filter(
        (bond) => bond.bond_type === type
      ).length;
      acc[type] = activeCount + toBeCancelledCount;
      return acc;
    }, {});
    setActiveCounts(counts);
  }, [toBeCancelledBonds]);

  useEffect(() => {
    calculateStatusCounts(bonds, selectedType);
    calculateActiveCounts(bonds);
  }, [bonds, selectedType, calculateStatusCounts, calculateActiveCounts]);


  const fetchBonds = async (newStart, newEnd) => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        apiClient(`/list-bonds/?status=Active&start=${newStart}&end=${newEnd}`),
        apiClient(`/list-bonds/?status=Approved&start=${newStart}&end=${newEnd}`),
        apiClient(`/list-bonds/?status=Rejected&start=${newStart}&end=${newEnd}`),
      ]);

      const allBonds = results.flatMap((r) =>
        r.status === "fulfilled" && Array.isArray(r.value) ? r.value : []
      );

      if (allBonds.length === 0) {
        setHasMore(false);
      } else {
        setBonds(allBonds);
        setStart(newEnd);
        setEnd(newEnd + 50);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching bonds:", error);
      setLoading(false);
    }
  };

  const fetchToBeCancelledBonds = async () => {
    try {
      setLoading(true);
      const response = await apiClient('/bonds-to-be-cancelled');
      setToBeCancelledBonds(response || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching to be cancelled bonds:", error);
      setToBeCancelledBonds([]);
      setLoading(false);
    }
  };

  const formatBondAmount = (amount) => {
    if (amount === null) return "$0.00";
    return `$${Number(amount).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const filteredData = (() => {
    const statusFilter = bondTabs[tabIndex]?.toLowerCase() || "active";

    if (statusFilter === "to be cancelled") {
      console.log("Selected Type:", selectedType);
      console.log("Search Query:", searchQuery);
      // Use the special API data for "To Be Cancelled" bonds
      const result = toBeCancelledBonds.filter((bond) => {
        const bondTypeMatch = !selectedType || bond.bond_type === selectedType;
        const principalsStr = Array.isArray(bond.principals)
          ? bond.principals.map((p) => String(p)).join(" ").toLowerCase()
          : (bond.principals != null ? String(bond.principals) : "").toLowerCase();
        const principalMatch = principalsStr.includes(searchQuery.toLowerCase())
          || bond.name?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        const lawFirmMatch = bond.law_firm_name?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        const bondNumberMatch = bond.bond_number?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        const lawFirmUserMatch = bond.law_firm_user_name?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        return bondTypeMatch && (principalMatch || lawFirmMatch || bondNumberMatch || lawFirmUserMatch);
      });
      console.log("Filtered To Be Cancelled Bonds:", result);
      return result;
    }

    // Use regular bonds data for other tabs
    return bonds.filter((bond) => {
      const bondTypeMatch = selectedType ? bond.bond_type === selectedType : true;
      const statusMatch = bond.status.toLowerCase() === statusFilter;
      const principalMatch =
        bond.principal_one?.toLowerCase().includes(searchQuery.toLowerCase()) ??
        false;

      return bondTypeMatch && statusMatch && principalMatch;
    });
  })();

  const currentRows = bondTabs[tabIndex]?.toLowerCase() === "to be cancelled"
    ? filteredData  // Show all cancelled bonds without pagination
    : filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );

  const handleBondTypeClick = (bondType) => {
    setselectedType(bondType);
    setTabIndex(0);
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Reset page when switching tabs to avoid pagination issues
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [stats, setStats] = useState({
    totalApprovedBonds: 0,
    averageProcessingTime: 0,
  });
  const [dataloading, setDataLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await apiClient("/average-processing-time/");
        setStats({
          totalApprovedBonds: data.total_approved_bonds || 0,
          averageProcessingTime: data.average_processing_time_minutes || 0,
        });
      } catch (error) {
        console.error("Failed to fetch processing time:", error);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const getEndorsementInstructions = (bond) => {
    const type = bond.bond_type;

    const typeSupports = {
      courtOrder: ["Probate", "Guardianship", "Trustee"],
      accounting: ["Guardianship", "Trustee"],
      vaLetter: ["VA"],
    };

    return [
      ...(typeSupports.courtOrder.includes(type)
        ? ["• Court Order or Endorsement Checklist"]
        : []),
      ...(typeSupports.accounting.includes(type)
        ? ["• Annual Accounting Documentation"]
        : []),
      ...(typeSupports.vaLetter.includes(type)
        ? ["• VA Letter of Endorsement"]
        : []),
    ];
  };

  const getCancellationInstructionsList = (bond) => {
    const amount = parseFloat(bond.bond_amount || 0);
    const type = bond.bond_type;
    if (type === "Probate") {
      if (amount <= 250000) {
        return [
          "Discharge from the court",
          "Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs",
        ];
      }
      return [
        "Final Accounting",
        "Discharge from the court",
        "Copy of verification of closing evidence from a court's online system",
      ];
    }
    if (type === "Guardianship") {
      const main =
        amount <= 1000000
          ? [
              "Final Accounting",
              "Discharge from the court",
              "Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs and/or beneficiaries",
            ]
          : [
              "Final Accounting",
              "Discharge from the court",
              "Copy of verification of closing evidence from a court's online system",
            ];
      return [
        ...main,
        "If a Successor Bond has been filed with the court:",
        "Guardianship - Evidence of death of the incompetent",
        "Minor Guardian - Evidence of the minor reaching the age of majority",
      ];
    }
    if (type === "VA") return ["Exoneration Letter from the VA"];
    if (type === "Trustee") {
      return [
        "Court's Release",
        "Final Accounting",
        "Evidence that either the beneficiary of trust has died or that trust assets have been fully dispersed to beneficiary(s) per the Trust",
      ];
    }
    if (type === "Blanket") return ["Email confirmation that renewal is not required."];
    return ["Upload appropriate closing document for this bond."];
  };

  const handleMarkCancellationComplete = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingCancellationComplete(true);
    try {
      const bondType = selectedBondToCancel.bond_type.toLowerCase();
      let endpoint = '';

      switch (bondType) {
        case 'blanket':
          endpoint = `/update-blanket-bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'guardianship':
          endpoint = `/update_guardianship_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'probate':
          endpoint = `/update_probate_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'trustee':
          endpoint = `/update_trustee_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'va':
          endpoint = `/update_va_bond/${selectedBondToCancel.bond_id}/`;
          break;
        default:
          throw new Error('Invalid bond type');
      }

      const payload = buildBondStatusPatchPayload(bondType, "Cancelled");

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond cancellation marked as completed successfully");
      setCancelDialogOpen(false);
      await fetchBonds(0, 50);
      await fetchToBeCancelledBonds();
      calculateStatusCounts(bonds, selectedType);
      calculateActiveCounts(bonds);
    } catch (error) {
      console.error("Error marking cancellation as complete:", error);
      toast.error("Failed to mark cancellation as completed");
    } finally {
      setIsMarkingCancellationComplete(false);
    }
  };

  const handleRevertCancellation = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingCancellationComplete(true);
    try {
      const bondType = selectedBondToCancel.bond_type.toLowerCase();
      let endpoint = '';

      switch (bondType) {
        case 'blanket':
          endpoint = `/update-blanket-bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'guardianship':
          endpoint = `/update_guardianship_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'probate':
          endpoint = `/update_probate_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'trustee':
          endpoint = `/update_trustee_bond/${selectedBondToCancel.bond_id}/`;
          break;
        case 'va':
          endpoint = `/update_va_bond/${selectedBondToCancel.bond_id}/`;
          break;
        default:
          throw new Error('Invalid bond type');
      }

      const payload = buildBondStatusPatchPayload(bondType, "Approved");

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond cancellation reverted successfully");
      setCancelDialogOpen(false);
      await fetchBonds(0, 50);
      await fetchToBeCancelledBonds();
      calculateStatusCounts(bonds, selectedType);
      calculateActiveCounts(bonds);
    } catch (error) {
      console.error("Error reverting cancellation:", error);
      toast.error("Failed to revert cancellation");
    } finally {
      setIsMarkingCancellationComplete(false);
    }
  };

  return (
    <>
      <Navbar />
      <Container
        maxWidth={false}
        disableGutters
        sx={{ py: 3, px: { xs: 2, sm: 3 } }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Approve Bonds
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Review bonds by type and status. Use search to quickly find a principal, law firm, or bond number.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              onClick={() => setStatsOpen(true)}
              startIcon={<BarChartIcon />}
              variant="outlined"
            >
              Stats
            </Button>
            <Button
              onClick={refreshData}
              startIcon={<RefreshIcon />}
              variant="contained"
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </Stack>
        </Box>

        <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Last 12 Months Stats</DialogTitle>
          <DialogContent>
            {dataloading ? (
              <CircularProgress />
            ) : (
              <>
                <Typography variant="body1">
                  Total Approved Bonds: <strong>{stats.totalApprovedBonds}</strong>
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Avg Processing Time: <strong>{formatTime(stats.averageProcessingTime)}</strong>
                </Typography>
              </>
            )}

            {docGenFlat.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Bond Generation Status
                  </Typography>
                  <Chip
                    size="small"
                    label={docGenFlat.filter((d) => d.doc_generation_status === "processing").length + " active"}
                    color="warning"
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  See where each bond is across every step of document generation, from
                  start to finish. Updates in real time; entries stay visible for 7 days.
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Bond #</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Triggered By</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Approved At</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {docGenGroups.map((group) => (
                        <React.Fragment key={group.date || group.display_date || "group"}>
                          <TableRow>
                            <TableCell colSpan={7} sx={{ bgcolor: "action.hover", py: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {group.display_date || group.date || "—"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          {(group.items || []).map((item) => {
                            const stepLabel = DOC_GEN_STEPS[getStepIndex(item.step)]?.label || "Processing...";
                            const isQueued = item.step === "queued" || !item.step;
                            return (
                              <TableRow key={item.bond_id}>
                                <TableCell>{item.bond_number || "N/A"}</TableCell>
                                <TableCell>{item.name || "N/A"}</TableCell>
                                <TableCell>{item.bond_type || "N/A"}</TableCell>
                                <TableCell>{item.triggered_by || "N/A"}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                  {formatApprovedAt(item.approved_at)}
                                </TableCell>
                                <TableCell>
                                  {item.doc_generation_status === "processing" && (
                                    <Chip
                                      size="small"
                                      label={stepLabel}
                                      color={isQueued ? "warning" : "info"}
                                      icon={<CircularProgress size={14} color="inherit" />}
                                    />
                                  )}
                                  {item.doc_generation_status === "completed" && (
                                    <Chip
                                      size="small"
                                      label="Completed"
                                      color="success"
                                      icon={<CheckCircleIcon />}
                                    />
                                  )}
                                  {item.doc_generation_status === "failed" && (
                                    <Chip
                                      size="small"
                                      label="Failed"
                                      color="error"
                                      icon={<ErrorOutlineIcon />}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.doc_generation_status === "processing" ? (
                                    <DocGenProgress step={item.step} />
                                  ) : (
                                    formatDuration(item.minutes_elapsed)
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Bond type selector */}
        <Box sx={{ mt: 2 }}>
          <ToggleButtonGroup
            value={selectedType}
            exclusive
            onChange={(_, v) => v && handleBondTypeClick(v)}
            sx={{
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                borderRadius: "999px !important",
                px: 2,
                py: 0.75,
                border: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            {bondTypes.map((type) => {
              const toCancelCount = toBeCancelledBonds.filter((b) => b.bond_type === type).length;
              const activeOnly = Math.max((activeCounts[type] || 0) - toCancelCount, 0);
              return (
                <ToggleButton key={type} value={type}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {type}
                    </Typography>
                    {activeOnly > 0 && <Chip size="small" color="primary" label={activeOnly} />}
                    {toCancelCount > 0 && <Chip size="small" color="error" label={toCancelCount} />}
                  </Stack>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Box>

        {selectedType && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {selectedType} Bonds
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {bondTabs.map((tab, index) => {
                  const tabKey = tab.toLowerCase();
                  const count = statusCounts[tabKey] || 0;
                  const displayLabel = tab === "Rejected" ? "Declined" : tab;
                  const isToCancel = tabKey === "to be cancelled";
                  const isDataCorrections = tab === "Data Corrections";
                  const dataCorrectionsNewCount = isDataCorrections
                    ? dataCorrectionsList.filter(
                        (r) =>
                          (r.bond_type || "").toString().trim() === (selectedType || "").toString().trim() &&
                          (r.status || "").toString().trim().toLowerCase() === "new"
                      ).length
                    : 0;
                  const showPill = !isDataCorrections && count > 0 && tab !== "Approved" && tab !== "Rejected";
                  const showDataCorrectionsNewChip = isDataCorrections && dataCorrectionsNewCount > 0;

                  return (
                    <Tab
                      key={index}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {displayLabel}
                          </Typography>
                          {showPill && (
                            <Chip
                              size="small"
                              color={isToCancel ? "error" : "primary"}
                              label={count}
                            />
                          )}
                          {showDataCorrectionsNewChip && (
                            <Chip
                              size="small"
                              color="warning"
                              label={dataCorrectionsNewCount}
                            />
                          )}
                        </Stack>
                      }
                    />
                  );
                })}
              </Tabs>

              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  label={bondTabs[tabIndex] === "Data Corrections" ? "Search by bond number" : bondTabs[tabIndex]?.toLowerCase() === "to be cancelled" ? "Search" : "Search by Principal"}
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    width: { xs: "100%", sm: 360 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          size="small"
                          onClick={() => setSearchQuery("")}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
            </Box>

            {bondTabs[tabIndex] === "Data Corrections" ? (
              dataCorrectionsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                  <CircularProgress />
                </Box>
              ) : (() => {
                const bondNumberSearch = (searchQuery || "").trim().toLowerCase();
                // Filter by selected bond type so Blanket view only shows Blanket corrections, etc.
                let filteredCorrections = dataCorrectionsList.filter((row) => (row.bond_type || "").toString().trim() === (selectedType || "").toString().trim());
                if (bondNumberSearch) {
                  filteredCorrections = filteredCorrections.filter((row) => (row.bond_number || "").toString().toLowerCase().includes(bondNumberSearch));
                }
                if (filteredCorrections.length === 0) {
                  return (
                    <Box sx={{ py: 5, textAlign: "center" }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>No correction entries</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {dataCorrectionsList.length === 0 ? "No data corrections have been recorded." : `No ${selectedType || ""} corrections${bondNumberSearch ? " for this bond number search." : "."}`}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <Box>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", maxHeight: 360 }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow sx={{ "& th": { backgroundColor: "#f0f0f0", color: "#111", fontWeight: 700, borderBottomColor: "rgba(0,0,0,0.12)" } }}>
                                <TableCell>Bond Number</TableCell>
                                <TableCell>Field</TableCell>
                                <TableCell>From</TableCell>
                                <TableCell>To</TableCell>
                                <TableCell>Requester</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody sx={{ "& tr:nth-of-type(odd)": { backgroundColor: "#fafafa" } }}>
                              {filteredCorrections.map((row, idx) => (
                                  <TableRow key={`${selectedType}-${idx}`}>
                                    <TableCell>{row.bond_number ?? "—"}</TableCell>
                                    <TableCell>{formatCorrectionFieldLabel(row.changes?.field)}</TableCell>
                                    <TableCell>{formatCorrectionFromTo(row.changes?.field, row.changes?.from)}</TableCell>
                                    <TableCell>{formatCorrectionFromTo(row.changes?.field, row.changes?.to)}</TableCell>
                                    <TableCell>{row.requester_info?.name ?? "—"} {row.requester_info?.role ? `(${row.requester_info.role})` : ""}</TableCell>
                                    <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</TableCell>
                                    <TableCell>{row.status ?? "—"}</TableCell>
                                    <TableCell align="center">
                                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                        <Tooltip title="View details" arrow>
                                          <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => {
                                              setSelectedDataCorrection(row);
                                              setDataCorrectionDetailOpen(true);
                                            }}
                                          >
                                            <VisibilityIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        {(row.status || "").toString().trim().toLowerCase() !== "complete" && (
                                          <Tooltip title="Mark as Complete" arrow>
                                            <span>
                                              <IconButton
                                                color="success"
                                                size="small"
                                                disabled={completingCorrectionId === getCorrectionId(row)}
                                                onClick={() => updateCorrectionStatusToComplete(row)}
                                              >
                                                {completingCorrectionId === getCorrectionId(row) ? (
                                                  <CircularProgress size={20} color="inherit" />
                                                ) : (
                                                  <CheckCircleIcon fontSize="small" />
                                                )}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        )}
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                  </Box>
                );
              })()
            ) : loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          backgroundColor: "#f0f0f0",
                          color: "#111",
                          fontWeight: 700,
                          borderBottomColor: "rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      {bondTabs[tabIndex]?.toLowerCase() === "to be cancelled" ? (
                        <>
                          <TableCell>Bond Type</TableCell>
                          <TableCell>Sub Category</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Principals</TableCell>
                          <TableCell>Bond Number</TableCell>
                          <TableCell align="right">Bond Amount</TableCell>
                          <TableCell>Law Firm Name</TableCell>
                          <TableCell>Attorney</TableCell>
                          <TableCell align="center">Action</TableCell>
                        </>
                      ) : (() => {
                        const currentTab = bondTabs[tabIndex]?.toLowerCase() || "active";
                        const isActive = currentTab === "active";
                        const isApproved = currentTab === "approved";
                        const isDeclined = currentTab === "rejected";

                        return (
                          <>
                            <TableCell>Bond Type</TableCell>
                            <TableCell>Sub Category</TableCell>
                            {(isApproved || isDeclined) && (
                              <TableCell>Bond Number</TableCell>
                            )}
                            <TableCell align="left">Underwriting</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Bond Amount</TableCell>
                            <TableCell>Principal</TableCell>
                            <TableCell>Law Firm Name</TableCell>
                            <TableCell>Attorney</TableCell>
                            <TableCell>Referral</TableCell>
                            {(isActive || isApproved) && (
                              <TableCell align="center">Action</TableCell>
                            )}
                          </>
                        );
                      })()}
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                    {filteredData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={(() => {
                          const currentTab = bondTabs[tabIndex]?.toLowerCase() || "active";
                          if (currentTab === "to be cancelled") return 9;
                          const isActive = currentTab === "active";
                          const isApproved = currentTab === "approved";
                          const isDeclined = currentTab === "rejected";
                          // Base columns: Bond Type, Sub Category, Underwriting, Name, Bond Amount, Principal, Law Firm Name, Attorney, Referral = 9
                          let cols = 9;
                          if (isApproved || isDeclined) cols += 1; // Bond Number
                          if (isActive || isApproved) cols += 1; // Action
                          return cols;
                        })()} align="center">
                          <Box sx={{ py: 5 }}>
                            <Typography variant="h6" sx={{ textAlign: "center", fontWeight: 800 }}>
                              No results
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 0.5 }}>
                              Try a different search term or switch tabs.
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                              <Button onClick={refreshData} startIcon={<RefreshIcon />} variant="outlined">
                                Refresh
                              </Button>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : bondTabs[tabIndex]?.toLowerCase() === "to be cancelled" ? (
                      // Render "To Be Cancelled" bonds with different structure
                      currentRows.map((bond, index) => (
                        <TableRow key={index}>
                          <TableCell>{bond.bond_type}</TableCell>
                          <TableCell>{bond.bond_sub_category}</TableCell>
                          <TableCell>{bond.name}</TableCell>
                          <TableCell>{Array.isArray(bond.principals) ? bond.principals.join(", ") : (bond.principals || "N/A")}</TableCell>
                          <TableCell>{bond.bond_number}</TableCell>
                          <TableCell align="right">{formatBondAmount(bond.bond_amount)}</TableCell>
                          <TableCell>{bond.law_firm_name}</TableCell>
                          <TableCell>{bond.law_firm_user_name}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="View Bond" arrow>
                                <IconButton
                                  color="primary"
                                  onClick={() => {
                                    let url = "";
                                    if (bond.bond_type === "VA") {
                                      url = `/vermost-va-bond-update/${bond.bond_id}`;
                                    } else if (bond.bond_type === "Trustee") {
                                      url = `/vermost-trustee-bond-update/${bond.bond_id}`;
                                    } else if (bond.bond_type === "Probate") {
                                      url = `/vermost-probate-bond-update/${bond.bond_id}`;
                                    } else if (bond.bond_type === "Blanket") {
                                      url = `/vermost-blanket-bond-update/${bond.bond_id}`;
                                    } else {
                                      url = `/vermost-guardianship-bond-update/${bond.bond_id}`;
                                    }
                                    if (url) {
                                      window.open(url, "_blank");
                                    }
                                  }}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              {bond.cancellation_document_link && (
                                <Tooltip title="Download Cancellation Documents" arrow>
                                  <IconButton
                                    sx={{ color: "grey.600" }}
                                    onClick={() => {
                                      window.open(bond.cancellation_document_link, "_blank");
                                    }}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Cancel Bond">
                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    setSelectedBondToCancel(bond);
                                    setCancellationFile(null);
                                    setCancelDialogOpen(true);
                                  }}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      // Render regular bonds with new structure
                      currentRows.map((bond, index) => {
                        const currentTab = bondTabs[tabIndex]?.toLowerCase() || "active";
                        const isActive = currentTab === "active";
                        const isApproved = currentTab === "approved";
                        const isDeclined = currentTab === "rejected";

                        const getBondUrl = () => {
                          if (bond.bond_type === "VA") {
                            return `/vermost-va-bond-update/${bond.bond_id}`;
                          } else if (bond.bond_type === "Trustee") {
                            return `/vermost-trustee-bond-update/${bond.bond_id}`;
                          } else if (bond.bond_type === "Probate") {
                            return `/vermost-probate-bond-update/${bond.bond_id}`;
                          } else if (bond.bond_type === "Blanket") {
                            return `/vermost-blanket-bond-update/${bond.bond_id}`;
                          } else {
                            return `/vermost-guardianship-bond-update/${bond.bond_id}`;
                          }
                        };

                        return (
                          <TableRow key={index}>
                            <TableCell>{bond.bond_type}</TableCell>
                            <TableCell>{bond.bond_sub_category || "N/A"}</TableCell>
                            {(isApproved || isDeclined) && (
                              <TableCell>{bond.bond_number || "N/A"}</TableCell>
                            )}
                            <TableCell align="left">
                              {(() => {
                                const chip = getUnderwritingChip(bond.underwriting_status);
                                return (
                                  <Chip
                                    size="small"
                                    label={chip.label}
                                    color={chip.color}
                                    variant={chip.color === "default" ? "outlined" : "filled"}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell>{bond.name}</TableCell>
                            <TableCell align="right">
                              {bond.last_endorsement_at ? (
                                <Typography variant="body2" color="primary">
                                  Endorsement Request:{" "}
                                  {formatBondAmount(bond.bond_amount)}
                                </Typography>
                              ) : (
                                formatBondAmount(bond.bond_amount)
                              )}
                            </TableCell>
                            <TableCell>{bond.principal_one}</TableCell>
                            <TableCell>{bond.law_firm_name || "N/A"}</TableCell>
                            <TableCell>{bond.law_firm_user_name || "N/A"}</TableCell>
                            <TableCell>
                              {bond.referral_source ? (
                                <Tooltip title={bond.referred_by ? `Referred by: ${bond.referred_by}` : ""} arrow>
                                  <Typography variant="body2" sx={{ fontSize: "0.8rem", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {bond.referral_source}
                                  </Typography>
                                </Tooltip>
                              ) : "—"}
                            </TableCell>
                            {(isActive || isApproved) && (
                              <TableCell align="center">
                                <Tooltip title="View Bond" arrow>
                                  <IconButton
                                    color="primary"
                                    onClick={() => {
                                      const url = getBondUrl();
                                      if (url) {
                                        window.open(url, "_blank");
                                      }
                                    }}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                {isActive && (
                                  <Tooltip title="Bond Endorsement" arrow>
                                    <IconButton
                                      color="secondary"
                                      onClick={() => {
                                        setSelectedBondToEndorse(bond);
                                        setEndorsementType("Increase");
                                        setNewBondAmount("");
                                        setEndorsementFile(null);
                                        setUpdatedName("");
                                        setEndorsementToCounty("");
                                        setEstimatedAssets("");
                                        setDwbFirstName("");
                                        setDwbMiddleName("");
                                        setDwbLastName("");
                                        setDwbSuffix("");
                                        setDwbAlias("");
                                        setEndorsementDialogOpen(true);
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {bondTabs[tabIndex]?.toLowerCase() !== "to be cancelled" && (
              <TablePagination
          ActionsComponent={TablePaginationActions}
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ mt: 2 }}
              />
            )}
          </Box >
        )
        }

        {
          hasMore && tabIndex !== 0 && tabIndex !== 3 && bondTabs[tabIndex] !== "Data Corrections" && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button variant="contained" onClick={() => fetchBonds(start, end)}>
                Load More
              </Button>
            </Box>
          )
        }
      </Container >

      {/* Endorsement Dialog */}
      < Dialog
        open={endorsementDialogOpen}
        onClose={() =>
          !isSubmittingEndorsement && setEndorsementDialogOpen(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>Bond Endorsement</DialogTitle>
        <DialogContent dividers>
          {selectedBondToEndorse?.bond_type === "Blanket" ? (
            <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
              Endorsement not allowed for Blanket.
            </Typography>
          ) : selectedBondToEndorse ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Endorsement Details
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Bond Number:</strong> {selectedBondToEndorse.bond_number ?? "N/A"}
              </Typography>
              <Typography sx={{ mb: 2 }}>
                <strong>Bond Type:</strong> {selectedBondToEndorse.bond_type}
              </Typography>

              <TextField
                label="Endorsement Type"
                select
                fullWidth
                value={endorsementType}
                onChange={(e) => setEndorsementType(e.target.value)}
                SelectProps={{ native: true }}
                required
                sx={{ mb: 2 }}
              >
                <option value="Increase">Increase</option>
                <option value="Decrease">Decrease</option>
                <option value="Name_Change_Principal">Name change (Principal)</option>
                <option value="Name_Change_County">Name change (County)</option>
                <option value="Name_Change_Deceased_Ward_Beneficiary">Name change (Deceased/Ward/Beneficiary)</option>
              </TextField>

              {isNameChangeEndorsement(endorsementType) ? (
                endorsementType === "Name_Change_County" ? (
                  <Autocomplete
                    options={counties}
                    loading={loadingCounties}
                    value={
                      endorsementToCounty
                        ? counties.find((c) => c.value === endorsementToCounty) || null
                        : null
                    }
                    onChange={(e, selected) =>
                      setEndorsementToCounty(selected ? selected.value : "")
                    }
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option && value && option.value === value.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="To County"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCounties ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    fullWidth
                    disabled={loadingCounties}
                    sx={{ mb: 3 }}
                  />
                ) : endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? (
                  (() => {
                    const labels = DWB_LABEL_MAP[selectedBondToEndorse?.bond_type] || DWB_LABEL_MAP.Probate;
                    if (labels.single) {
                      return (
                        <TextField
                          label={labels.prefix}
                          value={updatedName}
                          onChange={(e) => setUpdatedName(e.target.value)}
                          fullWidth
                          required
                          sx={{ mb: 3 }}
                        />
                      );
                    }
                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField label={`${labels.prefix} First Name`} value={dwbFirstName} onChange={(e) => setDwbFirstName(e.target.value)} fullWidth required />
                          <TextField label={`${labels.prefix} Middle Name`} value={dwbMiddleName} onChange={(e) => setDwbMiddleName(e.target.value)} fullWidth />
                        </Box>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField label={`${labels.prefix} Last Name`} value={dwbLastName} onChange={(e) => setDwbLastName(e.target.value)} fullWidth required />
                          <TextField label="Suffix" value={dwbSuffix} onChange={(e) => setDwbSuffix(e.target.value)} sx={{ maxWidth: 120 }} />
                        </Box>
                        <TextField label={labels.alias} value={dwbAlias} onChange={(e) => setDwbAlias(e.target.value)} fullWidth />
                      </Box>
                    );
                  })()
                ) : (
                  <TextField
                    label="Updated Name"
                    name="updatedName"
                    value={updatedName}
                    onChange={(e) => setUpdatedName(e.target.value)}
                    fullWidth
                    required
                    sx={{ mb: 3 }}
                  />
                )
              ) : (
                <TextField
                  label="New Bond Amount (Rounded Up)"
                  name="newBondAmount"
                  value={newBondAmount}
                  onChange={(e) => setNewBondAmount(e.target.value)}
                  fullWidth
                  required
                  InputProps={{
                    inputComponent: NumberFormatCustom,
                  }}
                  sx={{ mb: 3 }}
                />
              )}

              {(selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && (
                <TextField
                  label="Updated Estimated Assets (optional)"
                  type="text"
                  inputMode="decimal"
                  value={estimatedAssets}
                  onChange={(e) => setEstimatedAssets(e.target.value)}
                  fullWidth
                  placeholder="e.g. 250000"
                  sx={{ mb: 2 }}
                  helperText="Fill the updated estimated assets value if additional assets were discovered."
                />
              )}

              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Documents
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Acceptable Documentation:
              </Typography>
              <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
                {getEndorsementInstructions(selectedBondToEndorse).map(
                  (item, idx) => (
                    <li
                      key={idx}
                      style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                    >
                      {item}
                    </li>
                  )
                )}
              </ul>

              <Button
                variant="outlined"
                component="label"
                disabled={isSubmittingEndorsement}
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Upload Supporting Document
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    const validTypes = [
                      "application/pdf",
                      "application/msword",
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    ];
                    if (file && !validTypes.includes(file.type)) {
                      toast.error("Only PDF or Word documents are allowed.");
                      e.target.value = "";
                      return;
                    }
                    setEndorsementFile(file);
                    toast.success("File selected successfully!");
                  }}
                />
              </Button>
              {endorsementFile && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Selected File: {endorsementFile.name}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Allowed file types: PDF, DOC, DOCX
              </Typography>

              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Contact Information
              </Typography>
              <TextField
                label=" Name"
                name="contactName"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                fullWidth
                required
                placeholder="Full name"
                sx={{ mb: 2 }}
              />

              <TextField
                label=" Email"
                type="email"
                name="contactEmail"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                fullWidth
                required
                placeholder="name@example.com"
                sx={{ mb: 1 }}
              />
            </>
          ) : (
            <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
              No bond selected.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEndorsementDialogOpen(false)}
            disabled={isSubmittingEndorsement}
          >
            Close
          </Button>
          {selectedBondToEndorse &&
            selectedBondToEndorse.bond_type !== "Blanket" && (
              <>
                {(role === "internal_user" || role === "super_user") && (
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={!selectedBondToEndorse || isSubmittingEndorsement}
                  onClick={async () => {
                    if (!selectedBondToEndorse) return;
                    const getRiderUrl = () => {
                      switch (selectedBondToEndorse.bond_type) {
                        case "VA":
                          return `/release-rider-va/${selectedBondToEndorse.bond_id}`;
                        case "Trustee":
                          return `/release-rider-trustee/${selectedBondToEndorse.bond_id}`;
                        case "Probate":
                          return `/release-rider-probate/${selectedBondToEndorse.bond_id}`;
                        case "Blanket":
                          return `/release-rider-blanket/${selectedBondToEndorse.bond_id}`;
                        case "Guardianship":
                          return `/release-rider-guardianship/${selectedBondToEndorse.bond_id}`;
                        default:
                          return `/release-rider-guardianship/${selectedBondToEndorse.bond_id}`;
                      }
                    };
                    const isInternalOrSuper = role === "internal_user" || role === "super_user";
                    const countyOk = endorsementType !== "Name_Change_County" || !!endorsementToCounty;
                      const dwbOk = endorsementType !== "Name_Change_Deceased_Ward_Beneficiary" || (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? !!updatedName : !!(dwbFirstName || dwbLastName));
                      const nameChangeOk = endorsementType === "Name_Change_County" ? countyOk : (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? dwbOk : (endorsementType === "Name_Change_Principal" ? !!updatedName : true));
                      const hasDetails = endorsementType &&
                        contactName &&
                        contactEmail &&
                        (isNameChangeEndorsement(endorsementType) ? nameChangeOk : (!!newBondAmount && !!endorsementFile));

                      if (isInternalOrSuper && hasDetails) {
                        setIsSubmittingEndorsement(true);
                        const formData = new FormData();
                        formData.append("endorsement_type", endorsementType);
                        if (isNameChangeEndorsement(endorsementType)) {
                          if (endorsementType === "Name_Change_County") {
                            formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
                          } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
                            if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
                              formData.append("updated_name", updatedName);
                            } else {
                              formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
                              formData.append("updated_first_name", dwbFirstName);
                              formData.append("updated_middle_name", dwbMiddleName);
                              formData.append("updated_last_name", dwbLastName);
                              formData.append("updated_suffix", dwbSuffix);
                              formData.append("updated_alias", dwbAlias);
                            }
                        } else {
                          formData.append("updated_name", updatedName);
                        }
                      } else {
                        formData.append("updated_bond_amount", newBondAmount.replace(/[^\d.]/g, ""));
                      }
                      if (endorsementFile) formData.append("endorsement_file", endorsementFile);
                      formData.append("contact_name", contactName);
                      formData.append("contact_email", contactEmail);
                      const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
                      if ((selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && estAssetsNum != null) {
                        formData.append("estimated_assets", estAssetsNum);
                      }
                      try {
                        await apiClient(
                          `/endorse-bond/`,
                          "POST",
                          formData,
                          { bond_id: selectedBondToEndorse.bond_id },
                          { "Content-Type": "multipart/form-data" }
                        );
                        toast.success("Bond endorsed successfully!");
                        setEndorsementDialogOpen(false);
                        refreshData();
                        navigate(getRiderUrl());
                      } catch (error) {
                        console.error("Endorsement failed:", error);
                        toast.error(error.message || "Failed to endorse the bond.");
                      } finally {
                        setIsSubmittingEndorsement(false);
                      }
                    } else {
                      const url = getRiderUrl();
                      if (url) navigate(url);
                    }
                  }}
                >
                  Release Rider
                </Button>
                )}
                {role !== "internal_user" && role !== "super_user" && (
                <Button
                  variant="contained"
                    disabled={
                      isSubmittingEndorsement ||
                      ((!isNameChangeEndorsement(endorsementType) && !endorsementFile)) ||
                      (endorsementType === "Name_Change_County" && !endorsementToCounty) ||
                      (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" && !(DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? updatedName : (dwbFirstName || dwbLastName)))
                    }
                  onClick={async () => {
                      const countyOk = endorsementType !== "Name_Change_County" || !!endorsementToCounty;
                      const dwbOk = endorsementType !== "Name_Change_Deceased_Ward_Beneficiary" || (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? !!updatedName : !!(dwbFirstName || dwbLastName));
                      const nameChangeOk = endorsementType === "Name_Change_County" ? countyOk : (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? dwbOk : (endorsementType === "Name_Change_Principal" ? !!updatedName : true));
                    if (
                      !selectedBondToEndorse ||
                      (!isNameChangeEndorsement(endorsementType) && !endorsementFile) ||
                      (isNameChangeEndorsement(endorsementType) ? !nameChangeOk : !newBondAmount) ||
                      !endorsementType ||
                      !contactName ||
                      !contactEmail
                    ) {
                      toast.error(
                        isNameChangeEndorsement(endorsementType)
                          ? "Please fill all required fields and contact info."
                          : "Please fill all required fields, contact info, and upload the file."
                      );
                      return;
                    }

                    setIsSubmittingEndorsement(true);

                    const formData = new FormData();
                    formData.append("endorsement_type", endorsementType);
                    if (isNameChangeEndorsement(endorsementType)) {
                      if (endorsementType === "Name_Change_County") {
                        formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
                      } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
                        if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
                          formData.append("updated_name", updatedName);
                        } else {
                          formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
                          formData.append("updated_first_name", dwbFirstName);
                          formData.append("updated_middle_name", dwbMiddleName);
                          formData.append("updated_last_name", dwbLastName);
                          formData.append("updated_suffix", dwbSuffix);
                          formData.append("updated_alias", dwbAlias);
                        }
                      } else {
                        formData.append("updated_name", updatedName);
                      }
                    } else {
                      formData.append(
                        "updated_bond_amount",
                        newBondAmount.replace(/[^\d.]/g, "")
                      );
                    }
                    if (endorsementFile) formData.append("endorsement_file", endorsementFile);
                    formData.append("contact_name", contactName);
                    formData.append("contact_email", contactEmail);
                    const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
                    if ((selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && estAssetsNum != null) {
                      formData.append("estimated_assets", estAssetsNum);
                    }

                    try {
                      await apiClient(
                        `/endorse-bond/`,
                        "POST",
                        formData,
                        { bond_id: selectedBondToEndorse.bond_id },
                        {
                          "Content-Type": "multipart/form-data",
                        }
                      );

                      toast.success("Bond endorsed successfully!");
                      setEndorsementDialogOpen(false);
                      refreshData(); // Refresh the data after successful endorsement
                    } catch (error) {
                      console.error("Endorsement failed:", error);
                      toast.error(
                        error.message || "Failed to endorse the bond."
                      );
                    } finally {
                      setIsSubmittingEndorsement(false);
                    }
                  }}
                >
                  {isSubmittingEndorsement ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Submit Endorsement"
                  )}
                </Button>
                )}
              </>
            )}
        </DialogActions>
      </Dialog >

      {/* Cancellation Dialog */}
      < Dialog
        open={cancelDialogOpen}
        onClose={() => !isCancelling && setCancelDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2.5,
            fontWeight: 700,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Bond Cancellation
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => !isCancelling && setCancelDialogOpen(false)}
            edge="end"
            size="small"
            disabled={isCancelling}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {selectedBondToCancel && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Cancellation Details
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Bond Number:</strong> {selectedBondToCancel.bond_number ?? "N/A"}
              </Typography>
              <Typography sx={{ mb: 2 }}>
                <strong>Bond Type:</strong> {selectedBondToCancel.bond_type}
              </Typography>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                Documents
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Acceptable Documentation:
              </Typography>
              <ul style={{ paddingLeft: "24px", marginBottom: "20px", listStyleType: "disc" }}>
                {getCancellationInstructionsList(selectedBondToCancel).map(
                  (item, idx) => (
                    <li
                      key={idx}
                      style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                    >
                      {item}
                    </li>
                  )
                )}
              </ul>

              <Button
                variant="outlined"
                component="label"
                disabled={isCancelling}
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    const validTypes = [
                      "application/pdf",
                      "application/msword",
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    ];
                    if (file && !validTypes.includes(file.type)) {
                      toast.error("Only PDF or Word documents are allowed.");
                      e.target.value = "";
                      return;
                    }
                    setCancellationFile(file);
                    toast.success("File selected successfully!");
                  }}
                />
              </Button>
              {cancellationFile && (
                <Typography variant="caption" sx={{ display: "block", mb: 2 }}>
                  Selected File: {cancellationFile.name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: "100%" }}
            useFlexGap
            flexWrap="wrap"
          >
            {/* Secondary (state) actions on the left */}
            {selectedBondToCancel && (
              <Stack direction="row" spacing={1.25} flexWrap="wrap">
                <Button
                  onClick={handleMarkCancellationComplete}
                  color="error"
                  variant="outlined"
                  disabled={isCancelling || isMarkingCancellationComplete}
                  startIcon={isMarkingCancellationComplete && <CircularProgress size={16} />}
                >
                  {isMarkingCancellationComplete ? "Marking Cancelled..." : "Mark as Cancelled"}
                </Button>

                <Button
                  onClick={handleRevertCancellation}
                  color="primary"
                  variant="outlined"
                  disabled={isCancelling || isMarkingCancellationComplete}
                  startIcon={isMarkingCancellationComplete && <CircularProgress size={16} />}
                >
                  {isMarkingCancellationComplete ? "Reverting..." : "Revert Cancellation"}
                </Button>
              </Stack>
            )}

            {/* Primary actions on the right */}
            <Stack direction="row" spacing={1.25} justifyContent="flex-end">

              <Button
                variant="contained"
                color="primary"
                disabled={!cancellationFile || isCancelling}
                onClick={async () => {
                  if (!cancellationFile || !selectedBondToCancel) return;
                  setIsCancelling(true);
                  const formData = new FormData();
                  formData.append("cancellation_file", cancellationFile);
                  try {
                    await apiClient(
                      `/cancel-bond/`,
                      "POST",
                      formData,
                      { bond_id: selectedBondToCancel.bond_id },
                      { "Content-Type": "multipart/form-data" }
                    );
                    toast.success("Bond cancelled successfully!");
                    setCancelDialogOpen(false);
                    refreshData();
                  } catch (error) {
                    console.error(error);
                    toast.error(error.message || "Failed to cancel the bond.");
                  } finally {
                    setIsCancelling(false);
                  }
                }}
              >
                {isCancelling ? <CircularProgress size={22} color="inherit" /> : "Submit Cancellation"}
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog >

      {/* Data correction detail popup */}
      <Dialog
        open={dataCorrectionDetailOpen}
        onClose={() => {
          setDataCorrectionDetailOpen(false);
          setSelectedDataCorrection(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 1.5, mb: 2 }}>
          Correction details
        </DialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          {selectedDataCorrection && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Date</Typography>
                <Typography variant="body1">
                  {selectedDataCorrection.created_at ? new Date(selectedDataCorrection.created_at).toLocaleString() : "—"}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Bond type</Typography>
                <Typography variant="body1">{selectedDataCorrection.bond_type ?? "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Bond number</Typography>
                <Typography variant="body1">{selectedDataCorrection.bond_number ?? "—"}</Typography>
              </Box>
              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Change</Typography>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Field</Typography>
                <Typography variant="body1">{formatCorrectionFieldLabel(selectedDataCorrection.changes?.field)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>From</Typography>
                <Typography variant="body1">{formatCorrectionFromTo(selectedDataCorrection.changes?.field, selectedDataCorrection.changes?.from) || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>To</Typography>
                <Typography variant="body1">{formatCorrectionFromTo(selectedDataCorrection.changes?.field, selectedDataCorrection.changes?.to) || "—"}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Requester</Typography>
                <Typography variant="body1">
                  {selectedDataCorrection.requester_info?.name ?? "—"}
                  {selectedDataCorrection.requester_info?.role ? ` (${selectedDataCorrection.requester_info.role})` : ""}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>Status</Typography>
                <Typography variant="body1">{selectedDataCorrection.status ?? "—"}</Typography>
              </Box>
              {selectedDataCorrection.bond_id && (
                <>
                  <Divider />
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      const url = getBondRetrieveUrl(selectedDataCorrection.bond_type, selectedDataCorrection.bond_id);
                      if (url) window.open(url, "_blank");
                    }}
                  >
                    View bond details
                  </Button>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          {selectedDataCorrection && (selectedDataCorrection.status || "").toString().trim().toLowerCase() !== "complete" && (
            <Button
              variant="contained"
              color="success"
              startIcon={completingCorrectionId === getCorrectionId(selectedDataCorrection) ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
              disabled={completingCorrectionId === getCorrectionId(selectedDataCorrection)}
              onClick={() => updateCorrectionStatusToComplete(selectedDataCorrection)}
            >
              Mark as Complete
            </Button>
          )}
          <Button
            onClick={() => {
              setDataCorrectionDetailOpen(false);
              setSelectedDataCorrection(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}

export default ApproveBondsPage;